/**
 * mSTEM EXT-02 live block-shape preview (Phase 6 / Plan 06-03, MOCK-ONLY).
 *
 * THE only novel surface in Phase 6: a pure-SVG silhouette of a Scratch/OpenBlock
 * block in one of 4 shapes (COMMAND / REPORTER / BOOLEAN / HAT), filled with the
 * user-picked content color, with a centered auto-contrasted label and one white
 * rounded inset per declared input.
 *
 * HARD CONTRACT (06-UI-SPEC Pitfall 1 / threat T-06-03-02): this module is a
 * styled SVG mock. It MUST NEVER import or call the real workspace block engine
 * / its VM-bound instance / `workspace.*`. The block-builder-modal.test.jsx
 * negative assertion scans this source to enforce it. "Test trong workspace" =
 * render this preview + a toast; real blocks are produced by the backend later.
 *
 * Presentational arrow component (props-only, no Redux, no VM).
 */
import PropTypes from 'prop-types';
import React from 'react';

import {SHAPES} from '../../lib/mstem-mock/ext-block-types.js';

// --- label contrast (06-UI-SPEC §Typography luminance rule) ----------------
// Relative luminance of an sRGB hex color on 0..1: 0.2126R + 0.7152G + 0.0722B.
// luminance > 0.55 → dark text #1A1F36 (light block); else white #FFFFFF.
const LABEL_DARK = '#1A1F36';
const LABEL_LIGHT = '#FFFFFF';
const LUMINANCE_THRESHOLD = 0.55;

const hexToRgb = function (hex) {
    let h = (hex || '').replace('#', '');
    if (h.length === 3) {
        h = h.split('')
            .map(c => c + c)
            .join('');
    }
    const num = parseInt(h, 16);
    if (isNaN(num) || h.length !== 6) return {r: 0, g: 0, b: 0};
    return {
        r: (num >> 16) & 0xff,
        g: (num >> 8) & 0xff,
        b: num & 0xff
    };
};

const relativeLuminance = function (hex) {
    const {r, g, b} = hexToRgb(hex);
    return ((0.2126 * r) + (0.7152 * g) + (0.0722 * b)) / 255;
};

const labelColorFor = hex => (relativeLuminance(hex) > LUMINANCE_THRESHOLD ? LABEL_DARK : LABEL_LIGHT);

// --- shape silhouettes ------------------------------------------------------
// Base internal coordinate grid; viewBox 0 0 220 48 (06-UI-SPEC geometry contract).
const VIEW_W = 220;
const VIEW_H = 48;

// COMMAND: rounded-rect (rx=4) ~220×40 with a top concave notch + bottom convex bump.
const COMMAND_PATH =
    'M4 8 ' +
    'Q4 4 8 4 ' +
    'L24 4 ' +
    'L28 8 ' +
    'L44 8 ' +
    'L48 4 ' +
    'L212 4 ' +
    'Q216 4 216 8 ' +
    'L216 40 ' +
    'Q216 44 212 44 ' +
    'L48 44 ' +
    'L44 48 ' +
    'L28 48 ' +
    'L24 44 ' +
    'L8 44 ' +
    'Q4 44 4 40 ' +
    'Z';

// HAT: curved/rounded top cap (arc) + flat bottom with a bottom bump ~220×48.
const HAT_PATH =
    'M4 24 ' +
    'C4 4 60 0 110 0 ' +
    'C160 0 216 4 216 24 ' +
    'L216 40 ' +
    'Q216 44 212 44 ' +
    'L48 44 ' +
    'L44 48 ' +
    'L28 48 ' +
    'L24 44 ' +
    'L8 44 ' +
    'Q4 44 4 40 ' +
    'Z';

// BOOLEAN: hexagon polygon (angled left/right ends, flat top/bottom) ~150×32.
const BOOLEAN_POINTS = '12,8 138,8 150,24 138,40 12,40 0,24';

// REPORTER: pill — rect with rx = height/2 (~160×32), rendered as a <rect>.

const KNOWN_SHAPES = SHAPES.map(s => s.id);

const renderShape = function (shape, color) {
    switch (shape) {
    case 'reporter':
        return (
            <rect
                data-shape="reporter"
                x={4}
                y={8}
                width={160}
                height={32}
                rx={16}
                ry={16}
                fill={color}
            />
        );
    case 'boolean':
        return (
            <polygon
                data-shape="boolean"
                points={BOOLEAN_POINTS}
                fill={color}
            />
        );
    case 'hat':
        return (
            <path
                data-shape="hat"
                d={HAT_PATH}
                fill={color}
            />
        );
    case 'command':
    default:
        return (
            <path
                data-shape="command"
                d={COMMAND_PATH}
                fill={color}
            />
        );
    }
};

const BlockPreview = function ({shape, color, label, inputs}) {
    const safeShape = KNOWN_SHAPES.indexOf(shape) === -1 ? 'command' : shape;
    const labelFill = labelColorFor(color);
    const slots = inputs || [];

    return (
        <svg
            data-testid="block-preview"
            height="48"
            viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
            width="220"
            xmlns="http://www.w3.org/2000/svg"
        >
            {renderShape(safeShape, color)}
            <text
                dominantBaseline="middle"
                fill={labelFill}
                fontFamily="Inter, sans-serif"
                fontSize="13"
                fontWeight="600"
                textAnchor="middle"
                x={VIEW_W / 2}
                y={VIEW_H / 2}
            >
                {label}
            </text>
            {slots.map((input, idx) => (
                <rect
                    key={`slot-${idx}`}
                    data-slot="input"
                    fill="#FFFFFF"
                    height={20}
                    rx={4}
                    ry={4}
                    width={28}
                    x={VIEW_W - 40 - (idx * 32)}
                    y={(VIEW_H / 2) - 10}
                />
            ))}
        </svg>
    );
};

BlockPreview.propTypes = {
    color: PropTypes.string,
    inputs: PropTypes.arrayOf(PropTypes.shape({
        name: PropTypes.string,
        type: PropTypes.string
    })),
    label: PropTypes.string,
    shape: PropTypes.oneOf(['command', 'reporter', 'boolean', 'hat'])
};

BlockPreview.defaultProps = {
    color: '#9966FF',
    inputs: [],
    label: '',
    shape: 'command'
};

export default BlockPreview;
